/**
 * CRMCharts.jsx — Light Theme v6
 * Changes: light mode (white bg), chart hover effects, maximize icon + popup modal
 */

import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Popover, Tooltip as InfoTooltip, Spin, Checkbox } from "antd";
import { Link } from 'react-router-dom';
import FeatherIcon from 'feather-icons-react';
import { InfoCircleOutlined } from '@ant-design/icons';
import {
  BarChart, Bar, AreaChart, Area, Line,
  ScatterChart, Scatter, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Cell,
} from "recharts";
import { useSelector } from 'react-redux';
import { AccountsIcon, LeadIcon, DealIcon, ContactIcon } from '../../../components/Icons/Icons';

// ─── CSS ─────────────────────────────────────────────────────────────────────
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=DM+Mono:wght@400;500&display=swap');
*{box-sizing:border-box;}
@keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
@keyframes modalIn{from{opacity:0;transform:scale(.94)}to{opacity:1;transform:scale(1)}}
.crm-card{animation:fadeUp .4s cubic-bezier(.22,.68,0,1.1) both;transition:box-shadow .2s,transform .2s;}
// .crm-card:hover{box-shadow:0 12px 40px rgba(0,0,0,.14)!important;transform:translateY(-2px);}
.icon-btn{transition:background .15s,color .15s;}
.icon-btn:hover{background:#00b14f1f!important;color:#00B14F!important;}
.icon-btn.active{background:#00B14F!important;color:#fff!important;}
// .icon-btn:hover{background:#0070ff1f!important;color:#0070ff!important;}
// .icon-btn.active{color:#0070ff!important;}
.crm-legend::-webkit-scrollbar{width:4px;}
.crm-legend::-webkit-scrollbar-track{background:#F5F5F5;}
.crm-legend::-webkit-scrollbar-thumb{background:#D0D0D0;border-radius:4px;}
.hm-cell{transition:transform .15s,box-shadow .15s;border-radius:3px;cursor:default;}
.hm-cell:hover{transform:scale(1.28);box-shadow:0 2px 12px rgba(0,177,79,.35);z-index:10;position:relative;}
// .maximize-btn{opacity:0;transition:opacity .15s,background .15s;}
// .crm-card:hover .maximize-btn{opacity:1;}
.crm-card{.card-nav ul li.active:before {
    bottom: -8px !important;
    height: 3px !important;
  }}
.maximize-btn:hover{background:#0070ff1f!important;color:#0070ff!important;}
.modal-overlay{animation:fadeUp .15s ease both;}
.modal-card{animation:modalIn .2s cubic-bezier(.22,.68,0,1.1) both;}
.funnel-bar{transition:opacity .15s,filter .15s;}
.funnel-bar:hover{opacity:1!important;filter:brightness(1.08);}
@keyframes pieSlicePop{0%{transform:translate(0,0)}50%{transform:translate(var(--dx),var(--dy)) scale(1.04)}100%{transform:translate(var(--dx),var(--dy))}}
.pie-slice{transition:filter .18s,opacity .18s;cursor:pointer;}
.pie-slice:hover{filter:brightness(1.12) drop-shadow(0 4px 12px rgba(0,0,0,.18));opacity:.95;}
`;
let cssInjected = false;
function injectCSS() {
  if (cssInjected || typeof document === "undefined") return;
  const el = document.createElement("style");
  el.textContent = CSS;
  document.head.appendChild(el);
  cssInjected = true;
}

// ─── Palette & helpers ────────────────────────────────────────────────────────
const PAL = ["#00B14F", "#3B82F6", "#F59E0B", "#8B5CF6", "#EF4444", "#0891B2", "#EC4899", "#65A30D", "#EA580C", "#0D9488", "#A78BFA", "#34D399"];
function pal(n) { return Array.from({ length: n }, (_, i) => PAL[i % PAL.length]); }
function trunc(s, n = 18) { return s && s.length > n ? `${s.slice(0, n - 1)}…` : (s || ""); }
const createAxisFormatter = (source, currencySettings) => {
  return (value) => {
    if (value == null) return value;
    if (source !== "YES") return String(value);
    let formatted = value;
    if (Math.abs(value) >= 1000000) {
      formatted = `${(value / 1000000).toFixed(1).replace(".0", "")}M`
    } else if (Math.abs(value) >= 1000) {
      formatted = `${(value / 1000).toFixed(1).replace(".0", "")}K`;
    }
    const symbol = currencySettings?.symbol;
    return `${symbol} ${formatted} `;
  };
};
const StackTooltip = ({ payload, label, xFormatter, yFormatter, xyFormatter, TT }) => {
  if (!payload?.length) return null;
  const fullLabel = payload?.[0]?.payload?.fullName || label;
  return (
    <div style={{ ...TT.contentStyle }}>
      <div style={{ fontWeight: 700, marginBottom: 6, color: "#111" }}>
        {yFormatter ? yFormatter(fullLabel) : fullLabel}
      </div>
      {payload.map((p, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 12,
            color: "#555",
            marginBottom: 3,
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: p.fill || p.color,
            }}
          />
          <span style={{ fontWeight: 500 }}>
            {xyFormatter ? xyFormatter(p.dataKey) : p.dataKey?.toLocaleString?.() ?? p.dataKey}
          </span>
          <span>
            {xFormatter ? xFormatter(p.value) : p.value?.toLocaleString?.() ?? p.value}
          </span>
        </div>
      ))}
    </div>
  );
};
StackTooltip.propTypes = {
  payload: PropTypes.array,
  label: PropTypes.any,
  xFormatter: PropTypes.func,
  yFormatter: PropTypes.func,
  xyFormatter: PropTypes.func,
  TT: PropTypes.object,
};
// ─── Chart type keys ──────────────────────────────────────────────────────────
const T = {
  H_BAR: "h_bar", V_BAR: "v_bar", STK_H: "stk_h", STK_V: "stk_v",
  PIE: "pie", DONUT: "donut", LINE: "line", BUBBLE: "bubble", BAR_LINE: "bar_line",
};
const SubName = {
  "Donut": "T.DONUT", "Pie Chart": "T.PIE", "Line Graph": "T.LINE", "Vertical Bar": "T.V_BAR", "Bar+Line": "T.BAR_LINE",
  "Horizontal Bar": "T.H_BAR", "Stacked Vertical Bar": "T.STK_V", "Stacked Horizontal Bar": "T.STK_H", "BUBBLE": "T.BUBBLE"
};
// };
const TYPE_NAME = {
  "h_bar": "Horizontal Bar Chart", "v_bar": "Vertical Bar Chart", "stk_h": "Stacked Horizontal Bar Chart", "stk_v": "Stacked Vertical Bar Chart",
  "pie": "Pie Chart", "donut": "Donut Chart", "line": "Line Chart", "bubble": "Bubble Chart", "bar_line": "Bar+Line Chart"
};
// ─── Icons ────────────────────────────────────────────────────────────────────
const IOO = {
  "T.H_BAR": T.H_BAR,
  "T.V_BAR": T.V_BAR,
  "T.STK_H": T.STK_H,
  "T.STK_V": T.STK_V,
  "T.PIE": T.PIE,
  "T.DONUT": T.DONUT,
  "T.LINE": T.LINE,
  "T.BUBBLE": T.BUBBLE,
  "T.BAR_LINE": T.BAR_LINE
};
const IO = {
  [T.H_BAR]: <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.3"><rect x="2" y="3" width="10" height="2" rx="1" /><rect x="2" y="7" width="7" height="2" rx="1" /><rect x="2" y="11" width="12" height="2" rx="1" /></svg>,
  [T.V_BAR]: <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.3"><rect x="2" y="6" width="3" height="8" rx="1" /><rect x="6.5" y="3" width="3" height="11" rx="1" /><rect x="11" y="8" width="3" height="6" rx="1" /></svg>,
  [T.STK_H]: <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.2"><rect x="2" y="3" width="5" height="2" rx="1" /><rect x="7" y="3" width="4" height="2" rx="1" /><rect x="2" y="7" width="7" height="2" rx="1" /><rect x="9" y="7" width="3" height="2" rx="1" /><rect x="2" y="11" width="4" height="2" rx="1" /><rect x="6" y="11" width="6" height="2" rx="1" /></svg>,
  [T.STK_V]: <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.2"><rect x="2" y="9" width="3" height="5" rx="1" /><rect x="2" y="5" width="3" height="4" rx="1" /><rect x="6.5" y="6" width="3" height="8" rx="1" /><rect x="6.5" y="3" width="3" height="3" rx="1" /><rect x="11" y="7" width="3" height="7" rx="1" /><rect x="11" y="4" width="3" height="3" rx="1" /></svg>,
  [T.PIE]: <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.3"><circle cx="8" cy="8" r="5.5" /><line x1="8" y1="8" x2="8" y2="2.5" /><line x1="8" y1="8" x2="13" y2="9.5" /></svg>,
  [T.DONUT]: <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.3"><circle cx="8" cy="8" r="5.5" /><circle cx="8" cy="8" r="2.2" /></svg>,
  [T.LINE]: <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.5"><polyline points="2,12 5,7 8,9 11,4 14,6" /></svg>,
  [T.BAR_LINE]: <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.3"><rect x="2" y="8" width="3" height="6" rx="1" /><rect x="6.5" y="5" width="3" height="9" rx="1" /><rect x="11" y="9" width="3" height="5" rx="1" /><polyline points="2,6 6,4 10,7 14,3" strokeWidth="1.5" /></svg>,
  [T.BUBBLE]: <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.3"><circle cx="5" cy="11" r="3" /><circle cx="11" cy="6" r="2" /><circle cx="9" cy="12" r="1.5" /></svg>,
};
const IF = {
  [T.H_BAR]: <svg viewBox="0 0 16 16" width="15" height="15" fill="currentColor"><rect x="2" y="3" width="10" height="2" rx="1" /><rect x="2" y="7" width="7" height="2" rx="1" /><rect x="2" y="11" width="12" height="2" rx="1" /></svg>,
  [T.V_BAR]: <svg viewBox="0 0 16 16" width="15" height="15" fill="currentColor"><rect x="2" y="6" width="3" height="8" rx="1" /><rect x="6.5" y="3" width="3" height="11" rx="1" /><rect x="11" y="8" width="3" height="6" rx="1" /></svg>,
  [T.STK_H]: <svg viewBox="0 0 16 16" width="15" height="15" fill="currentColor"><rect x="2" y="3" width="5" height="2" rx="1" /><rect x="7" y="3" width="4" height="2" rx="1" opacity=".6" /><rect x="2" y="7" width="7" height="2" rx="1" /><rect x="9" y="7" width="3" height="2" rx="1" opacity=".6" /><rect x="2" y="11" width="4" height="2" rx="1" /><rect x="6" y="11" width="6" height="2" rx="1" opacity=".6" /></svg>,
  [T.STK_V]: <svg viewBox="0 0 16 16" width="15" height="15" fill="currentColor"><rect x="2" y="9" width="3" height="5" rx="1" /><rect x="2" y="5" width="3" height="4" rx="1" opacity=".6" /><rect x="6.5" y="6" width="3" height="8" rx="1" /><rect x="6.5" y="3" width="3" height="3" rx="1" opacity=".6" /><rect x="11" y="7" width="3" height="7" rx="1" /><rect x="11" y="4" width="3" height="3" rx="1" opacity=".6" /></svg>,
  [T.PIE]: <svg viewBox="0 0 16 16" width="15" height="15" fill="currentColor"><path d="M8 2a6 6 0 1 0 6 6H8V2Z" /><path d="M9.5 2.3A6 6 0 0 1 14 7.5H9.5V2.3Z" opacity=".5" /></svg>,
  [T.DONUT]: <svg viewBox="0 0 16 16" width="15" height="15" fill="currentColor"><path fillRule="evenodd" d="M8 3a5 5 0 1 0 5 5 5 5 0 0 0-5-5Zm0 3a2 2 0 1 1-2 2 2 2 0 0 1 2-2Z" /></svg>,
  [T.LINE]: <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8"><polyline points="2,12 5,7 8,9 11,4 14,6" /></svg>,
  [T.BAR_LINE]: <svg viewBox="0 0 16 16" width="15" height="15" fill="currentColor"><rect x="2" y="8" width="3" height="6" rx="1" /><rect x="6.5" y="5" width="3" height="9" rx="1" /><rect x="11" y="9" width="3" height="5" rx="1" /><polyline points="2,6 6,4 10,7 14,3" fill="none" stroke="currentColor" strokeWidth="1.5" /></svg>,
  [T.BUBBLE]: <svg viewBox="0 0 16 16" width="15" height="15" fill="currentColor"><circle cx="5" cy="11" r="3" opacity=".8" /><circle cx="11" cy="6" r="2" opacity=".6" /><circle cx="9" cy="12" r="1.5" opacity=".5" /></svg>,
};
// ─── Maximize icon ────────────────────────────────────────────────────────────
const MaximizeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="10,2 14,2 14,6" /><polyline points="6,14 2,14 2,10" />
    <line x1="14" y1="2" x2="9" y2="7" /><line x1="2" y1="14" x2="7" y2="9" />
  </svg>
);
// ─── Light tooltip / axis styles ──────────────────────────────────────────────
const TT = {
  contentStyle: { background: "#FFFFFF", border: "1px solid #E5E7EB", borderRadius: 10, fontFamily: "Noto Sans", fontSize: 12, color: "#111", padding: "8px 12px", boxShadow: "0 4px 16px rgba(0,0,0,.08)" },
  labelStyle: { color: "#555", fontWeight: 600 },
  itemStyle: { color: "#555" },
  cursor: { fill: "rgba(0,0,0,0.04)" },
};
const AX = { tick: { fill: "#9CA3AF", fontSize: 10, fontFamily: "Noto Sans", }, axisLine: false, tickLine: false };
const GRID = { stroke: "#F0F0F0", vertical: false };
// ─── Animated Pie / Donut ─────────────────────────────────────────────────────
function AnimatedPieDonut({ type, labels, data, datasets, cs, height, xFormatter, yFormatter }) {
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [tooltip, setTooltip] = useState(null);
  const isDonut = type === T.DONUT;

  const flat = datasets.length === 1
    ? datasets[0].data.map((v, i) => ({ name: labels[i] || `Item ${i + 1} `, value: v }))
    : labels.map((l, i) => ({ name: l, value: datasets.reduce((s, ds) => s + (ds.data[i] || 0), 0) }));

  const datasetColors = data?.chartTypeAdd === "LegendIcon" ? datasets?.[0]?.backgroundColor : [];
  const total = flat.reduce((s, d) => s + d.value, 0) || 1;
  const CX = 50;
  const CY = 50;
  const R = 38;
  const rInner = isDonut ? 22 : 0;
  const EXPLODE = 5;
  const GAP_DEG = isDonut ? 1.2 : 0.5;

  let cursor = -90;
  const slices = flat.map((d, i) => {
    const pct = d.value / total;
    const deg = pct * 360 - GAP_DEG;
    const startDeg = cursor + GAP_DEG / 2;
    const endDeg = startDeg + deg;
    cursor += pct * 360;

    const toRad = (degrees) => (degrees * Math.PI) / 180;
    const x1 = CX + R * Math.cos(toRad(startDeg));
    const y1 = CY + R * Math.sin(toRad(startDeg));
    const x2 = CX + R * Math.cos(toRad(endDeg));
    const y2 = CY + R * Math.sin(toRad(endDeg));
    const ix1 = CX + rInner * Math.cos(toRad(startDeg));
    const iy1 = CY + rInner * Math.sin(toRad(startDeg));
    const ix2 = CX + rInner * Math.cos(toRad(endDeg));
    const iy2 = CY + rInner * Math.sin(toRad(endDeg));
    const largeArc = deg > 180 ? 1 : 0;
    const midDeg = startDeg + deg / 2;
    const midRad = toRad(midDeg);
    const dx = EXPLODE * Math.cos(midRad);
    const dy = EXPLODE * Math.sin(midRad);

    let dPath;
    if (isDonut) {
      dPath = [
        `M ${ix1} ${iy1} `,
        `L ${x1} ${y1} `,
        `A ${R} ${R} 0 ${largeArc} 1 ${x2} ${y2} `,
        `L ${ix2} ${iy2} `,
        `A ${rInner} ${rInner} 0 ${largeArc} 0 ${ix1} ${iy1} `,
        "Z"
      ].join(" ");
    } else {
      dPath = [
        `M ${CX} ${CY} `,
        `L ${x1} ${y1} `,
        `A ${R} ${R} 0 ${largeArc} 1 ${x2} ${y2} `,
        "Z"
      ].join(" ");
    }

    return { ...d, i, dPath, dx, dy, midDeg, midRad, pct, color: data?.chartTypeAdd === "LegendIcon" ? datasetColors[i] : cs[i] };
  });

  return (
    <div style={{ position: "relative", width: "100%", height }}>
      <svg
        viewBox="0 0 100 100"
        width="100%"
        height={height}
        style={{ display: "block" }}
      >
        <defs>
          {slices.map((s, i) => (
            <filter key={i} id={`pie-glow-${i}`} x="-30%" y="-30%" width="160%" height="160%">
              <feGaussianBlur stdDeviation="1.5" result="blur" />
              <feFlood floodColor={s.color} floodOpacity="0.45" result="color" />
              <feComposite in="color" in2="blur" operator="in" result="glow" />
              <feMerge><feMergeNode in="glow" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
          ))}
        </defs>
        {slices.map((s) => {
          const hovered = hoveredIdx === s.i;
          return (
            <path
              key={s.i}
              d={s.dPath}
              fill={s.color}
              stroke="#fff"
              strokeWidth={isDonut ? 0.4 : 0.3}
              className="pie-slice"
              style={{
                transform: hovered ? `translate(${s.dx}px, ${s.dy}px) scale(1.04)` : "translate(0,0) scale(1)",
                transformOrigin: `${CX}px ${CY} px`,
                transformBox: "fill-box",
                transition: "transform .22s cubic-bezier(.34,1.56,.64,1), filter .18s, opacity .18s",
                filter: hovered ? `brightness(1.13) drop - shadow(0 2px 4px rgba(0, 0, 0, .22))` : "none",
                opacity: hoveredIdx !== null && !hovered ? 0.72 : 1,
              }}
              onMouseEnter={(e) => {
                setHoveredIdx(s.i);
                const rect = e.currentTarget.closest("svg").getBoundingClientRect();
                setTooltip({ x: e.clientX - rect.left, y: e.clientY - rect.top, name: s.name, value: s.value, pct: s.pct, color: s.color });
              }}
              onMouseMove={(e) => {
                const rect = e.currentTarget.closest("svg").getBoundingClientRect();
                setTooltip(t => t ? { ...t, x: e.clientX - rect.left, y: e.clientY - rect.top } : null);
              }}
              onMouseLeave={() => { setHoveredIdx(null); setTooltip(null); }}
            />
          );
        })}
        {isDonut && (
          <text x={CX} y={CY + 1.5} textAnchor="middle" dominantBaseline="middle"
            style={{ fontFamily: "'Noto Sans', sans-serif", fontSize: 6, fontWeight: 700, fill: "#111", pointerEvents: "none" }}>
            {hoveredIdx !== null ? `${Math.round((slices[hoveredIdx]?.pct ?? 0) * 100)}% ` : `${flat.length} `}
          </text>
        )}
        {isDonut && (
          <text x={CX} y={CY + 6} textAnchor="middle" dominantBaseline="middle"
            style={{ fontFamily: "'Noto Sans', sans-serif", fontSize: 3.5, fill: "#9CA3AF", pointerEvents: "none" }}>
            {/* {hoveredIdx !== null ? trunc(slices[hoveredIdx]?.name, 14) : "items"} */}
            {hoveredIdx !== null
              ? trunc(
                yFormatter
                  ? yFormatter(slices[hoveredIdx]?.name)
                  : slices[hoveredIdx]?.name,
                14
              )
              : "items"}
          </text>
        )}
      </svg>
      {tooltip && (
        <div style={{
          position: "absolute",
          left: tooltip.x + 10,
          top: tooltip.y - 36,
          background: "#fff",
          border: "1px solid #E5E7EB",
          borderRadius: 10,
          padding: "7px 12px",
          fontFamily: "'Noto Sans', sans-serif",
          fontSize: 12,
          pointerEvents: "none",
          boxShadow: "0 4px 16px rgba(0,0,0,.1)",
          whiteSpace: "nowrap",
          zIndex: 20,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: tooltip.color, display: "inline-block" }} />
            {/* <span style={{ fontWeight: 700, color: "#111" }}>{trunc(tooltip.name, 22)}</span> */}
            <span style={{ fontWeight: 700, color: "#111" }}>
              {yFormatter
                ? yFormatter(tooltip.name)
                : tooltip.name}
            </span>
          </div>
          <div style={{ color: "#555" }}>
            {/* {tooltip.value.toLocaleString()}  */}
            {xFormatter ? xFormatter(tooltip.value) : tooltip.value.toLocaleString()}
            <span style={{ color: "#9CA3AF" }}>({Math.round(tooltip.pct * 100)}%)</span></div>
        </div>
      )}
    </div>
  );
}
AnimatedPieDonut.propTypes = {
  type: PropTypes.string.isRequired,
  labels: PropTypes.arrayOf(PropTypes.string).isRequired,
  datasets: PropTypes.object.isRequired,
  cs: PropTypes.arrayOf(PropTypes.string).isRequired,
  height: PropTypes.number.isRequired,
  xFormatter: PropTypes.func.isRequired,
  yFormatter: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
};
const withAlpha = (color, alpha) => {
  // If it's already hsl(...)
  if (color.startsWith('hsl')) {
    return color.replace('hsl', 'hsla').replace(')', `, ${alpha})`);
    // hsl(120, 60%, 50%) → hsla(120, 60%, 50%, 0.9)
  }
  // If it's hex #rrggbb
  const r = parseInt(color.slice(1, 3), 16);
  const g = parseInt(color.slice(3, 5), 16);
  const b = parseInt(color.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};
const accountColorMap = {};
let accountCount = 0;
const generateColorForAccount = (accountName) => {
  if (!accountColorMap[accountName]) {
    // Golden angle ensures good color spacing
    const hue = (accountCount * 137.508) % 360;
    const saturation = 60; // %
    const lightness = 50;  // %
    const color = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    accountColorMap[accountName] = color;
    accountCount += 1;
  }
  return accountColorMap[accountName];
};
const pipelineColorMap = {};
let pipelineCount = 0;
const generateColorForAccount1 = (accountName) => {
  if (!pipelineColorMap[accountName]) {
    // Golden angle ensures good color spacing
    const hue = (pipelineCount * 137.508) % 360;
    const saturation = 60; // %
    const lightness = 50;  // %
    const color = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    pipelineColorMap[accountName] = color;
    pipelineCount += 1;
  }
  return pipelineColorMap[accountName];
};
// ─── ChartRenderer ────────────────────────────────────────────────────────────
function ChartRenderer({ type, data, height, ogData, column, row, row1, currencySettings }) {
  const xFormatter = createAxisFormatter(column, currencySettings);
  const yFormatter = createAxisFormatter(row1, currencySettings);
  const xyFormatter = createAxisFormatter(row, currencySettings);
  if (type === T.BUBBLE) {
    const cs = pal(data.datasets.length);
    return (
      <ResponsiveContainer width="100%" height={height}>
        <ScatterChart margin={{ top: 8, right: 16, bottom: 16, left: 0 }}>
          <CartesianGrid stroke="#F0F0F0" />
          <XAxis dataKey="x" type="number" name="Deal Size" {...AX} label={{ value: "Deal Size ($k)", position: "insideBottom", offset: -4, fill: "#9CA3AF", fontSize: 9 }} />
          <YAxis dataKey="y" type="number" name="Win Prob" {...AX} label={{ value: "Win %", angle: -90, position: "insideLeft", fill: "#9CA3AF", fontSize: 9 }} />
          <Tooltip {...TT} content={({ payload }) => {
            if (!payload?.length) return null;
            const d = payload[0]?.payload;
            return <div style={{ ...TT.contentStyle }}><div style={{ color: "#111", fontWeight: 700, marginBottom: 3 }}>{payload[0].name}</div><div style={{ color: "#555", fontSize: 11 }}>Size: ${d?.x}k · Win: {d?.y}%</div></div>;
          }} />
          {data.datasets.map((ds, i) => (
            <Scatter key={ds.label} name={ds.label} data={ds.data} fill={cs[i]} fillOpacity={0.75} stroke={cs[i]} />
          ))}
        </ScatterChart>
      </ResponsiveContainer>
    );
  }
  const { labels, datasets } = data;
  const cs = pal(Math.max(datasets?.length, labels?.length));
  const chartData = labels.map((l, i) => ({
    name: trunc(String(l), 12),
    fullName: String(l),
    ...Object.fromEntries(datasets.map(ds => [ds.label || "Value", ds.data[i] ?? 0])),
  }));
  const cm = { data: chartData, margin: { top: 8, right: 12, bottom: 4, left: -14 } };
  if (type === T.STK_H) {
    const stk = type === T.STK_H;
    const hd = labels.map((l, i) => {
      const row = {
        name: trunc(String(l), 14),
        fullName: String(l),
      };

      datasets.forEach((ds) => {
        row[ds.groupLables] = ds.data?.[i] ?? 0;
      });

      return row;
    });

    return (
      <ResponsiveContainer width="100%" height={height}>
        <BarChart layout="vertical" data={hd} margin={{ top: 4, right: 14, bottom: 4, left: 4 }}>
          <CartesianGrid stroke="#F0F0F0" horizontal={false} />
          <XAxis type="number" {...AX} tickFormatter={xFormatter} />
          <YAxis type="category" tickFormatter={yFormatter} dataKey="name" width={88} tick={{ fill: "#9CA3AF", fontSize: 10, fontFamily: "Noto Sans", }} axisLine={false} tickLine={false} />
          {/* <Tooltip {...TT} formatter={(v) => xFormatter(v)} labelFormatter={(label, payload) => {
  const full = payload?.[0]?.payload?.fullName;
  return yFormatter(full || label);
}} /> */}
          {/* <Tooltip
            {...TT}
            content={({ payload, label }) => {
              if (!payload?.length) return null;

              return (
                <div style={{ ...TT.contentStyle }}>
                  <div style={{ fontWeight: 700, marginBottom: 6, color: "#111" }}>
                    {label}
                  </div>

                  {payload.map((p, i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: 12,
                        color: "#555",
                        marginBottom: 3,
                      }}
                    >
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          background: p.fill || p.color,
                          display: "inline-block",
                        }}
                      />

                      <span style={{ fontWeight: 500 }}>
                        {p.dataKey}:
                      </span>

                      <span>
                        {p.value?.toLocaleString?.() ?? p.value}
                      </span>
                    </div>
                  ))}
                </div>
              );
            }}
          /> */}
          <Tooltip
            {...TT}
            content={(props) => (
              <StackTooltip
                {...props}
                xFormatter={xFormatter}
                yFormatter={yFormatter}
                xyFormatter={xyFormatter}
                TT={TT}
              />
            )}
          />
          {datasets.length === 1
            ? <Bar dataKey={datasets[0].groupLables || "Value"} radius={[0, 4, 4, 0]} maxBarSize={20} stackId={stk ? "s" : undefined}>
              {hd.map((_, i) => <Cell key={i} fill={cs[i]} />)}
            </Bar>
            : datasets.map((ds, i) => (
              <Bar key={ds.groupLables} dataKey={ds.groupLables} fill={cs[i]}
                radius={stk && i === datasets.length - 1 ? [0, 4, 4, 0] : [0, 0, 0, 0]} maxBarSize={18} stackId={stk ? "s" : undefined} />
            ))
          }
        </BarChart>
      </ResponsiveContainer>
    );
  }
  if (type === T.STK_V) {
    const stk = type === T.STK_V;
    const stackedData = labels.map((label, i) => {
      const row = {
        name: trunc(String(label), 12),
        fullName: String(label),
      };

      datasets.forEach((ds) => {
        row[ds.groupLables] = ds.data?.[i] ?? 0;
      });

      return row;
    });
    return (
      <ResponsiveContainer width="100%" height={height}>
        <BarChart {...cm} data={stackedData}>
          <CartesianGrid {...GRID} />
          <XAxis dataKey="name" {...AX} tickFormatter={yFormatter} />
          <YAxis {...AX} tickFormatter={xFormatter} />
          {/* <Tooltip {...TT} formatter={(v) => xFormatter(v)} labelFormatter={(label, payload) => {
  const full = payload?.[0]?.payload?.fullName;
  return yFormatter(full || label);
}} /> */}
          {/* <Tooltip
            {...TT}
            content={({ payload, label }) => {
              if (!payload?.length) return null;

              return (
                <div style={{ ...TT.contentStyle }}>
                  <div style={{ fontWeight: 700, marginBottom: 6, color: "#111" }}>
                    {label}
                  </div>

                  {payload.map((p, i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: 12,
                        color: "#555",
                        marginBottom: 3,
                      }}
                    >
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          background: p.fill || p.color,
                          display: "inline-block",
                        }}
                      />

                      <span style={{ fontWeight: 500 }}>
                        {p.dataKey}:
                      </span>

                      <span>
                        {p.value?.toLocaleString?.() ?? p.value}
                      </span>
                    </div>
                  ))}
                </div>
              );
            }}
          /> */}
          <Tooltip
            {...TT}
            content={(props) => (
              <StackTooltip
                {...props}
                xFormatter={xFormatter}
                yFormatter={yFormatter}
                xyFormatter={xyFormatter}
                TT={TT}
              />
            )}
          />
          {datasets.length === 1
            ? <Bar dataKey={datasets[0].groupLables || "Value"} radius={[4, 4, 0, 0]} maxBarSize={32} stackId={stk ? "s" : undefined}>
              {chartData.map((_, i) => <Cell key={i} fill={cs[i]} />)}
            </Bar>
            : datasets.map((ds, i) => (
              <Bar key={ds.groupLables} dataKey={ds.groupLables} fill={cs[i]}
                radius={stk && i === datasets.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} maxBarSize={26} stackId={stk ? "s" : undefined} />
            ))
          }
        </BarChart>
      </ResponsiveContainer>
    );
  }
  if (type === T.BAR_LINE) {
    const [d0, d1] = datasets;
    return (
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart {...cm}>
          <CartesianGrid {...GRID} />
          <XAxis dataKey="name" {...AX} />
          {/* <YAxis yAxisId="l" {...AX} tickFormatter={xFormatter} /> */}
          {/* <YAxis yAxisId="r" orientation="right" {...AX} tickFormatter={xFormatter} /> */}
          <YAxis {...AX} tickFormatter={xFormatter} />
          <Tooltip {...TT} labelFormatter={(label, payload) => {
            const full = payload?.[0]?.payload?.fullName;
            return full || label;
          }} formatter={(v) => xFormatter(v)} />
          {d0 && (
            <Bar
              dataKey={d0.label}
              fill={cs[0]}
              fillOpacity={0.85}
              radius={[4, 4, 0, 0]}
              maxBarSize={28}
            />
          )}

          {/* ✅ Line on SAME axis */}
          {d1 && (
            <Line
              type="monotone"
              dataKey={d1.label}
              stroke={cs[1]}
              strokeWidth={2.5}
              dot={{ r: 3, fill: cs[1], strokeWidth: 0 }}
              activeDot={{ r: 6, fill: cs[1], stroke: "#fff", strokeWidth: 2 }}
            />
          )}
          {/* {d0 && <Bar yAxisId="l" dataKey={d0.label} fill={cs[0]} fillOpacity={0.85} radius={[4, 4, 0, 0]} maxBarSize={28} />}
          {d1 && <Line yAxisId="r" type="monotone" dataKey={d1.label} stroke={cs[1]} strokeWidth={2.5}
            dot={{ r: 3, fill: cs[1], strokeWidth: 0 }} activeDot={{ r: 6, fill: cs[1], stroke: "#fff", strokeWidth: 2 }} />} */}
        </ComposedChart>
      </ResponsiveContainer>
    );
  }
  if (type === T.V_BAR) {
    const datam = (ogData?.chartTypeAdd === "LegendIcon" && ogData?.pageFrom2 === "Email") ?
      datasets[0].data.map((value, index) => ({
        value,
        fill: datasets[0].backgroundColor[index]
      })) : null
    const isAccountColored = ogData?.colorByAccount === true;
    const isPipelineColored = ogData?.colorByAccount1 === true;

    const accountColors = isAccountColored
      ? data.labels.map((label) => {  // label is a string
        const deal = ogData?.AllDeal?.find(d => d.dealName === label || d.label === label);
        const accountName = deal?.accountName || label;
        return generateColorForAccount(accountName);
      }) :
      isPipelineColored ?
        data.labels.map((label) => {  // label is a string
          const deal = ogData?.AllDeal?.find(d => d.dealName === label || d.label === label);
          const accountName = deal?.pipelineStage || label;
          return generateColorForAccount1(accountName);
        })
        : null;

    const alphas = [0.9, 0.8, 0.7];
    return (
      <ResponsiveContainer width="100%" height={height}>
        <BarChart {...cm}>
          <CartesianGrid {...GRID} />
          <XAxis dataKey="name" {...AX} tickFormatter={yFormatter} />
          <YAxis {...AX} tickFormatter={xFormatter} />
          <Tooltip {...TT} formatter={(v) => xFormatter(v)} labelFormatter={(label, payload) => {
            const full = payload?.[0]?.payload?.fullName;
            return yFormatter(full || label);
          }} />
          {datasets.length === 1
            ? (
              ogData?.chartTypeAdd === "LegendIcon" && ogData?.pageFrom2 === "Email" ?
                <Bar dataKey={datasets[0].label || "Value"} radius={[4, 4, 0, 0]} maxBarSize={32}>
                  {datam.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar> :
                <Bar
                  dataKey={datasets[0].label || "Value"}
                  fill={cs[0]} // ✅ single color
                  radius={[4, 4, 0, 0]}
                  maxBarSize={32}
                />
            )
            : datasets.map((ds, dsIndex) => (
              <Bar
                key={ds.label}
                dataKey={ds.label}
                fill={!isAccountColored ? cs[dsIndex] :
                  !isPipelineColored ? cs[dsIndex] :
                    undefined}
                radius={[0, 0, 0, 0]}
                maxBarSize={26}
              >
                {(isAccountColored || isPipelineColored) && data.labels.map((_, index) => (
                  <Cell
                    key={index}
                    fill={withAlpha(accountColors[index], alphas[dsIndex] ?? 0.8)}
                  />
                ))}
              </Bar>
            ))
          }
        </BarChart>
      </ResponsiveContainer>
    );
  }
  if (type === T.H_BAR) {
    const hd = labels.map((l, i) => ({
      name: trunc(String(l), 14),
      fullName: String(l),
      ...Object.fromEntries(
        datasets.map(ds => [ds.label || "Value", ds.data[i] ?? 0])
      ),
    }));

    return (
      <ResponsiveContainer width="100%" height={height}>
        <BarChart layout="vertical" data={hd} margin={{ top: 4, right: 14, bottom: 4, left: 4 }}>
          <CartesianGrid stroke="#F0F0F0" horizontal={false} />
          <XAxis type="number" {...AX} tickFormatter={xFormatter} />
          <YAxis tickFormatter={yFormatter}
            type="category"
            dataKey="name"
            width={88}
            tick={{ fill: "#9CA3AF", fontSize: 10, fontFamily: "Noto Sans" }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip {...TT} formatter={(v) => xFormatter(v)} labelFormatter={(label, payload) => {
            const full = payload?.[0]?.payload?.fullName;
            return yFormatter(full || label);
          }} />

          {datasets.length === 1
            ? (
              <Bar
                dataKey={datasets[0].label || "Value"}
                fill={cs[0]} // ✅ single color
                radius={[0, 4, 4, 0]}
                maxBarSize={20}
              />
            )
            : datasets.map((ds, i) => (
              <Bar
                key={ds.label}
                dataKey={ds.label}
                fill={cs[i]}
                radius={[0, 0, 0, 0]}
                maxBarSize={18}
              />
            ))
          }
        </BarChart>
      </ResponsiveContainer>
    );
  }
  if (type === T.LINE) {
    const c = cs[0];
    return (
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart {...cm}>
          <defs>
            <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={c} stopOpacity={0.18} />
              <stop offset="95%" stopColor={c} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid {...GRID} />
          <XAxis dataKey="name" {...AX} tickFormatter={yFormatter} />
          <YAxis {...AX} tickFormatter={xFormatter} />
          <Tooltip {...TT} formatter={(v) => xFormatter(v)} labelFormatter={(label, payload) => {
            const full = payload?.[0]?.payload?.fullName;
            return yFormatter(full || label);
          }} />
          <Area type="monotone" dataKey={datasets[0]?.label || "Value"} stroke={c} strokeWidth={2.5}
            fill="url(#areaGrad)" dot={{ r: 3, fill: c, strokeWidth: 0 }} activeDot={{ r: 6, fill: c, stroke: "#fff", strokeWidth: 2 }} />
        </AreaChart>
      </ResponsiveContainer>
    );
  }
  if (type === T.PIE || type === T.DONUT) {
    return <AnimatedPieDonut type={type} labels={labels} datasets={datasets} cs={cs} data={ogData} height={height} xFormatter={xFormatter} yFormatter={yFormatter} />;
  }
  return null;
}
ChartRenderer.propTypes = {
  type: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  currencySettings: PropTypes.object.isRequired,
  height: PropTypes.number.isRequired,
  column: PropTypes.string.isRequired,
  row: PropTypes.string.isRequired,
  row1: PropTypes.string.isRequired,
  ogData: PropTypes.object.isRequired,
};
const AutoFitValue = ({ children, maxFontSize = 18, minFontSize = 10 }) => {
  const containerRef = React.useRef(null);
  const textRef = React.useRef(null);

  React.useEffect(() => {
    const el = containerRef.current;
    const textEl = textRef.current;
    if (!el || !textEl) return;

    const resize = () => {
      let fontSize = maxFontSize; // ✅ start from max (not large/unlimited)
      textEl.style.fontSize = `${fontSize}px`;

      while (textEl.scrollWidth > el.offsetWidth && fontSize > minFontSize) {
        fontSize -= 1;
        textEl.style.fontSize = `${fontSize}px`;
      }
    };

    resize();

    const observer = new ResizeObserver(resize);
    observer.observe(el);

    return () => observer.disconnect();
  }, [children, maxFontSize, minFontSize]);

  return (
    <div ref={containerRef} style={{ width: "100%" }}>
      <div
        ref={textRef}
        style={{
          fontWeight: 600,
          color: "#111111",
          lineHeight: 1,
          fontFamily: "Noto Sans",
          whiteSpace: "nowrap",
        }}
      >
        {children}
      </div>
    </div>
  );
};
AutoFitValue.propTypes = {
  children: PropTypes.node,
  maxFontSize: PropTypes.number.isRequired,
  minFontSize: PropTypes.number.isRequired,
};
// ─── Legend panel ─────────────────────────────────────────────────────────────
function LegendPanel({ type, data, showonlyLegend, currencySettings, showLegendValues = true, }) { // hideLegendLabels = false 
  const isPieish = [T.PIE, T.DONUT].includes(type);
  const isSingleColors = [T.H_BAR, T.V_BAR, T.LINE].includes(type);
  const isStacked = [T.STK_H, T.STK_V].includes(type);
  const isBarLine = [T.BAR_LINE].includes(type);
  const isBarLine2 = data?.graphType1 === "Bar+Line";
  const isBubble = type === T.BUBBLE;
  let items = [];
  if (isBarLine2) {
    const cs = pal(data?.graphDatas?.datasets.length);
    items = data?.graphDatas?.datasets.map((ds, i) => ({ label: ds.label || `S${i + 1} `, color: cs[i] }));
  } else if (isPieish) {
    const cs = pal(data?.graphDatas?.labels.length);
    const values = data?.graphDatas?.datasets[0]?.data || [];
    items = data?.graphDatas?.labels.map((l, i) => ({ label: l, color: cs[i], value: values[i] || 0 }));
  } else if (isSingleColors) {
    if (data?.colorByAccount && data?.AllDeal) {
      const uniqueAccounts = [...new Set(data.AllDeal.map(d => d.accountName))];
      items = uniqueAccounts.map(accountName => ({
        label: accountName,
        color: generateColorForAccount(accountName),
      }));
    }
    else if (data?.colorByAccount1 && data?.AllDeal) {
      const uniqueAccounts = [...new Set(data.AllDeal.map(d => d.pipelineStage))];
      items = uniqueAccounts.map(accountName => ({
        label: accountName,
        color: generateColorForAccount(accountName),
      }));
    } else {
      const singleColor = pal(1)[0];
      const values = data?.graphDatas?.datasets[0]?.data || [];
      items = data?.graphDatas?.labels.map((l, i) => ({ label: l, color: singleColor, value: values[i] || 0 }));
    }
  } else if (isStacked) {
    const ds = data?.graphDatas?.datasets || [];
    const cs = pal(ds.length);
    items = ds.map((d, i) => ({
      label: d.groupLables || d.label || `Series ${i + 1}`,
      color: cs[i],
    }));
  } else if (isBubble) {
    const cs = pal(data?.graphDatas?.datasets.length);
    items = data?.graphDatas?.datasets.map((ds, i) => ({ label: ds.label || `S${i + 1} `, color: cs[i] }));
  } else if (isBarLine) {
    const cs = pal(data?.graphDatas?.datasets.length);
    items = data?.graphDatas?.datasets.map((ds, i) => ({ label: ds.label || `S${i + 1} `, color: cs[i] }));
  }
  const formatValue = (Currecny, val) => {
    if (Currecny === "YES") {
      return `${currencySettings?.symbol} ${Number(val).toLocaleString()} `
    }
    if (Currecny === "PERCENTAGE") {
      return `${val} %`;
    }
    return val;
  };
  return (
    <div style={{ width: 144, flexShrink: 0, borderLeft: "1px solid #F0F0F0", display: "flex", flexDirection: "column", padding: "15px 5px 5px", overflow: "hidden" }}> {/*  background: "#FAFAFA", */}
      {showonlyLegend ? <></> : <>
        {data?.graphDatas?.LegendType !== "" &&
          <div style={{ marginBottom: 12, paddingBottom: 12, borderBottom: "1px solid #EEEEEE", flexShrink: 0 }}> {/* textAlign: "center", */}
            <div style={{ fontSize: 12, color: "#868eae", fontWeight: 600, marginTop: 4, fontFamily: "Noto Sans", }}>
              {data?.graphDatas?.LegendType === "Currency" ? "Total Value" :
                data?.graphDatas?.LegendType === "TotalScore" ? "Total Score" :
                  data?.graphDatas?.LegendType === "CurrentScore" ? "Current Score" :
                    data?.graphDatas?.LegendType === "TotalSent" ? "Total Sent" :
                      data?.graphDatas?.LegendType === "TotalOpened" ? "Total Opened" :
                        "Total Count"}
              <AutoFitValue>
                {data?.graphDatas?.LegendType === "Currency" && currencySettings?.symbol} {data?.graphDatas?.LegendType === "TotalScore" || data?.graphDatas?.LegendType === "CurrentScore"
                  ? Number(data?.graphDatas?.Sumtotal).toFixed(currencySettings?.decimalOfCurrency)
                  : data?.graphDatas?.Sumtotal?.toLocaleString()}
              </AutoFitValue>
            </div>
          </div>}</>}
      {isBarLine &&
        data?.groupLable.map((item, i) => (
          <div key={i} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: "1px solid #EEEEEE", flexShrink: 0 }}> {/* textAlign: "center", */}
            <div style={{ fontSize: 12, color: "#868eae", fontWeight: 600, marginTop: 4, fontFamily: "Noto Sans", }}>
              {item.lable}
              <AutoFitValue>
                {data?.graphColumnIsCurrency === "YES" ? `${currencySettings?.symbol} ` : ""}{item?.value?.toLocaleString()}
              </AutoFitValue>
            </div>
          </div>))}
      {(isBarLine2 || data?.graphType1 === "Bar+Line2") &&
        data?.groupLable.map((item, i) => (
          <div key={i} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: "1px solid #EEEEEE", flexShrink: 0 }}> {/* textAlign: "center", */}
            <div style={{ fontSize: 12, color: "#868eae", fontWeight: 600, marginTop: 4, fontFamily: "Noto Sans", }}>
              {item.lable}
              <AutoFitValue>
                {data?.graphColumnIsCurrency === "YES" ? `${currencySettings?.symbol} ` : ""}{item?.value?.toLocaleString()}
              </AutoFitValue>
            </div>
          </div>))}
      <div style={{ fontSize: 9, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: ".1em", fontFamily: "Noto Sans", marginBottom: 7, flexShrink: 0 }}>Legend</div>
      <div className="crm-legend" style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6, minHeight: 0 }}>
        {data?.chartTypeAdd === "LegendIcon" ? (
          data?.pageFrom2 === "Email" ?
            (<>{data?.graphDatas?.datasets[0].data.map((value, index) => (
              <div key={index} title={`${data?.graphDatas?.labels[index]}${showLegendValues ? `: ${value}` : ''}`}
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, minWidth: 0, flexShrink: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0, flex: 1, overflow: "hidden" }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: data?.graphDatas?.datasets[0].backgroundColor[index], flexShrink: 0, display: "inline-block" }} />
                  <span style={{ fontSize: 10, color: "#6B7280", fontFamily: "Noto Sans", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", lineHeight: 1.3 }}>
                    {data?.graphDatas?.labels[index]}
                  </span>
                </div>
                {showLegendValues && (
                  <span style={{ fontSize: 10, color: "#9CA3AF", fontFamily: "Noto Sans", flexShrink: 0, fontWeight: 600 }}>
                    {value}
                  </span>
                )}
              </div>
            ))}</>
            ) : (<div className="sent-emial-data">
              <div className="sent-emial-box" >
                {data?.graphDatas?.datasets[0].data.map((value, index) => {
                  return (
                    <div className="sent-emial-item" key={index}>
                      <div className="sent-emial-icon" style={{ color: data?.graphDatas?.datasets[0].backgroundColor[index] }}>
                        {data?.graphDatas?.labels[index] === "leads" ? <LeadIcon />
                          : data?.graphDatas?.labels[index] === "contacts" ? <ContactIcon />
                            : data?.graphDatas?.labels[index] === "accounts" ? <AccountsIcon />
                              : data?.graphDatas?.labels[index] === "deals" ? <DealIcon /> : ""}</div>
                      <div className="sent-emial-content">
                        <h4 style={{ marginBottom: 0 }}>
                          <strong>{value}</strong>
                        </h4>
                        <p>{data?.graphDatas?.labels[index]}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>))
          : isBarLine2 ? (<>
            {items.map((item, i) => (
              <div
                key={i} title={formatValue(data?.graphGroupIsCurrecny, item.label)}
                style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0, flexShrink: 0 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: item.color, flexShrink: 0, display: "inline-block" }} />
                <span
                  style={{ fontSize: 10, color: "#6B7280", fontFamily: "Noto Sans", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", flex: 1, lineHeight: 1.3 }}>
                  {formatValue(data?.graphGroupIsCurrecny, item.label)}
                </span>
              </div>
            ))}
          </>) : isPieish || isSingleColors ? ([...items].sort((a, b) => {
            if (a.label === "Undefined") return 1;
            if (b.label === "Undefined") return -1;
            return b.value - a.value;
          }).map((item, i) => (
            <div key={i} title={`${formatValue(data?.graphRowIsCurrecny, item.label)}${showLegendValues ? `: ${formatValue(data?.graphColumnIsCurrency, item.value)}` : ''}`}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, minWidth: 0, flexShrink: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0, flex: 1, overflow: "hidden" }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: item.color, flexShrink: 0, display: "inline-block" }} />
                <span style={{ fontSize: 10, color: "#6B7280", fontFamily: "Noto Sans", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", lineHeight: 1.3 }}>
                  {formatValue(data?.graphRowIsCurrecny, item.label)}
                </span>
              </div>
              {showLegendValues && !data?.colorByAccount && !data?.colorByAccount1 && (
                <span style={{ fontSize: 10, color: "#9CA3AF", fontFamily: "Noto Sans", flexShrink: 0, fontWeight: 600 }}>
                  {formatValue(data?.graphColumnIsCurrency, item.value)}
                </span>
              )}
            </div>
          ))) : isStacked ?
            (items.map((item, i) => (
              <div
                key={i} title={formatValue(data?.graphGroupIsCurrecny, item.label)}
                style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0, flexShrink: 0 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: item.color, flexShrink: 0, display: "inline-block" }} />
                <span
                  style={{ fontSize: 10, color: "#6B7280", fontFamily: "Noto Sans", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", flex: 1, lineHeight: 1.3 }}>
                  {formatValue(data?.graphGroupIsCurrecny, item.label)}
                </span>
              </div>
            ))) : isBarLine ?
              (<>{items.map((item, i) => (
                <div
                  key={i} title={formatValue(data?.graphGroupIsCurrecny, item.label)}
                  style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0, flexShrink: 0 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: item.color, flexShrink: 0, display: "inline-block" }} />
                  <span
                    style={{ fontSize: 10, color: "#6B7280", fontFamily: "Noto Sans", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", flex: 1, lineHeight: 1.3 }}>
                    {formatValue(data?.graphGroupIsCurrecny, item.label)}
                  </span>
                </div>
              ))}</>) : ""}
      </div >
    </div >
  );
}
LegendPanel.propTypes = {
  type: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  showonlyLegend: PropTypes.bool.isRequired,
  currencySettings: PropTypes.object.isRequired,
  showLegendValues: PropTypes.bool,
  // hideLegendLabels: PropTypes.bool,
};
// ─── Icon sidebar ─────────────────────────────────────────────────────────────
function IconSidebar({ activeType, onSwitch, COMPAT }) {
  const all = [...(COMPAT || [])];
  return (
    <div style={{ width: 34, flexShrink: 0, borderRight: "1px solid #F0F0F0", display: "flex", flexDirection: "column", alignItems: "center", paddingTop: 8, gap: 2, background: "#FAFAFA", overflowY: "auto" }}>
      {all.map(icon => {
        const t = IOO[icon]
        const active = activeType === t;
        return (
          <button type="button" key={t} className={`icon-btn${active ? " active" : ""} `} title={TYPE_NAME[t]} onClick={() => onSwitch(t)}
            style={{ width: 26, height: 26, borderRadius: 6, border: "none", background: "transparent", color: active ? "#fff" : "#9CA3AF", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, flexShrink: 0 }}>
            {active ? IF[t] : IO[t]}
          </button>
        );
      })}
    </div>
  );
}
IconSidebar.propTypes = {
  activeType: PropTypes.string.isRequired,
  onSwitch: PropTypes.func.isRequired,
  COMPAT: PropTypes.arrayOf(PropTypes.string),
};
// ─── Pager ────────────────────────────────────────────────────────────────────
function Pager({ page, total, onPrev, onNext, itemsPerPage, totalItems }) {
  if (total <= 1) return null;
  const from = page * itemsPerPage + 1;
  const to = Math.min((page + 1) * itemsPerPage, totalItems);
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 14px 9px", borderTop: "1px solid #F0F0F0", flexShrink: 0 }}> {/* background: "#FAFAFA" */}
      <span style={{ fontSize: 11, color: "#9CA3AF", fontFamily: "Noto Sans", }}>{from}–{to} of {totalItems}</span>
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <button type="button" onClick={onPrev} disabled={page === 0} style={{ width: 24, height: 24, borderRadius: 6, border: "1px solid #E5E7EB", background: page === 0 ? "#F5F5F5" : "#FFFFFF", color: page === 0 ? "#D0D0D0" : "#0070ff", cursor: page === 0 ? "default" : "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center" }}>‹</button>
        <span style={{ width: 22, height: 24, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, background: "#0070ff", color: "#fff", borderRadius: 6, fontFamily: "Noto Sans", }}>{page + 1}</span>
        <button type="button" onClick={onNext} disabled={page >= total - 1} style={{ width: 24, height: 24, borderRadius: 6, border: "1px solid #E5E7EB", background: page >= total - 1 ? "#F5F5F5" : "#FFFFFF", color: page >= total - 1 ? "#D0D0D0" : "#0070ff", cursor: page >= total - 1 ? "default" : "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center" }}>›</button>
      </div>
    </div>
  );
}
Pager.propTypes = {
  page: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  onPrev: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  itemsPerPage: PropTypes.number.isRequired,
  totalItems: PropTypes.number.isRequired,
};
// ─── Maximize Modal ───────────────────────────────────────────────────────────
function MaximizeModal({ currencySettings, setPaginationState, title, data, activeType, onClose, page, pageSize, showonlyLegend, showLegendValues, hideLegendLabels, paginationState, hasPager, totalPages, totalItems }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);
  const isPieish = [T.PIE, T.DONUT].includes(activeType);
  const currentPage = (paginationState[data?.graphId] || 0);
  const totalLabels = data?.graphDatas?.labels || [];
  const pagedLabels = totalLabels.slice(
    currentPage * pageSize,
    (currentPage + 1) * pageSize
  );
  const pagedDatasets = data?.graphDatas?.datasets.map(ds => ({
    ...ds,
    data: ds.data.slice(currentPage * pageSize, (currentPage + 1) * pageSize),
  }));
  const pagedGraphData =
  {
    ...data?.graphDatas,
    labels: pagedLabels,
    datasets: pagedDatasets,
  }
  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onClose(); }}
      role="button"
      tabIndex={0}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 24
      }}
    >
      <div
        className="modal-card"
        onClick={e => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="button"
        tabIndex={0}
        // aria-modal="true"
        style={{
          background: "#FFFFFF", borderRadius: 20, border: "1px solid #E5E7EB",
          boxShadow: "0 24px 80px rgba(0,0,0,.18)", width: "min(900px,95vw)", maxHeight: "85vh",
          display: "flex", flexDirection: "column", overflow: "hidden"
        }}
      >
        <div style={{ padding: "14px 18px", borderBottom: "1px solid #F0F0F0", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: "#111", fontFamily: "Noto Sans", }}>{title}</span>
          <button type="button" onClick={onClose} style={{ width: 28, height: 28, borderRadius: 8, border: "1px solid #E5E7EB", background: "#F9F9F9", color: "#6B7280", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, lineHeight: 1 }}>×</button>
        </div>
        <div style={{ display: "flex", flex: 1, overflow: "hidden", minHeight: 0 }}>
          <div style={{ flex: 1, padding: "20px 16px", minWidth: 0, overflow: "auto" }}>
            <ChartRenderer type={activeType} ogData={data} data={isPieish ? data.graphDatas : pagedGraphData} currencySettings={currencySettings} height={440} column={data.graphColumnIsCurrency} row1={data.graphRowIsCurrecny} row={data.graphGroupIsCurrecny} />
          </div>
          <LegendPanel type={activeType} data={data} showonlyLegend={showonlyLegend} currencySettings={currencySettings} showLegendValues={showLegendValues} hideLegendLabels={hideLegendLabels} />
        </div>
        {(hasPager && totalPages > 1) && <Pager page={page} total={totalPages} itemsPerPage={pageSize} totalItems={totalItems}
          onPrev={() => {
            setPaginationState((prev) => ({
              ...prev,
              [data.graphId]: Math.max(0, (prev[data.graphId] ?? 0) - 1),
            }));
          }}
          onNext={() => {
            setPaginationState((prev) => ({
              ...prev,
              [data.graphId]: Math.min(totalPages - 1, (prev[data.graphId] ?? 0) + 1),
            }));
          }} />}
      </div>
    </div>
  );
}
MaximizeModal.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  currencySettings: PropTypes.object.isRequired,
  activeType: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  showonlyLegend: PropTypes.bool.isRequired,
  paginationState: PropTypes.object.isRequired,
  hasPager: PropTypes.bool.isRequired,
  totalPages: PropTypes.number.isRequired,
  totalItems: PropTypes.number.isRequired,
  setPaginationState: PropTypes.func.isRequired,
  showLegendValues: PropTypes.bool,
  hideLegendLabels: PropTypes.bool,
};
export function ChartCard({ currencySettings, handleQualifiedChange2, isQualified, setPaginationState, infoTitle, paginationState, data, initialType, graphDataIndex, pageSize, height, style, delay, COMPAT, graphopenUID,
  handleActiveChange,
  pagefrom,
  actions,
  openPage, graphDataStandardIndex,
  handleOpenPageChange,
  showonlyLegend = false,
  showLegend = true,
  showLegendValues = true,
  hideLegendLabels = false,
  showPager = true,
  showHeader = true, Content,
  showMaximize = true,
  titleNode, }) {
  useEffect(() => { injectCSS(); }, []);
  const [activeType, setActiveType] = useState(IOO[initialType]);
  const [maximized, setMaximized] = useState(false);
  const isPieish = [T.PIE, T.DONUT].includes(activeType);
  const hasPager = showPager && pageSize && data?.graphDatas?.labels && !isPieish;
  const totalPages = hasPager
    ? Math.ceil((data.graphDatas?.labels?.length ?? 0) / pageSize)
    : 1;
  const totalItems = data?.graphDatas?.labels ? data?.graphDatas?.labels.length : 0;
  const cardHeight = 45 + height + (hasPager ? 45 : 45);
  const currentPage = (paginationState[data?.graphId] || 0);
  const totalLabels = data?.graphDatas?.labels || [];
  const pagedLabels = totalLabels.slice(
    currentPage * pageSize,
    (currentPage + 1) * pageSize
  );
  const pagedDatasets = data?.graphDatas?.datasets.map(ds => ({
    ...ds,
    data: ds.data.slice(currentPage * pageSize, (currentPage + 1) * pageSize),
  }));
  const pagedGraphData =
  {
    ...data?.graphDatas,
    labels: pagedLabels,
    datasets: pagedDatasets,
  }
  useEffect(() => {
    setActiveType(IOO[initialType]);
  }, [initialType]);
  return (
    <>
      <div className="crm-card" style={{ marginBottom: 25, background: "#FFFFFF", borderRadius: 16, border: "0.1px solid #cdcdcd", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 1px 10px #00000026", animationDelay: `${delay} ms`, height: cardHeight, minHeight: cardHeight, maxHeight: cardHeight, ...style }}>
        {showHeader && (<div style={{ padding: "11px 14px 10px", borderBottom: "1px solid #F0F0F0", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0, height: 44 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#111111", fontFamily: "Noto Sans", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", flex: 1, letterSpacing: "0.01em" }} title={infoTitle}>
            {titleNode ? (
              <div className='Info-withTitle' title={data.graphName}>
                <span className='Info-withTitle-1'>
                  {titleNode}
                </span>
                {infoTitle && <InfoTooltip
                  overlayClassName="ToolTip-Forcast"
                  title={
                    <div className="report-info">
                      {infoTitle}
                    </div>
                  }
                >
                  <InfoCircleOutlined className='Info-withTitle-3' />
                </InfoTooltip>}
              </div>
            ) : (
              <div className='Info-withTitle' title={data.graphName}>
                <span className='Info-withTitle-1'>
                  <span className="div-ellipsis Info-withTitle-2">{data.graphName}</span>
                </span>
                {infoTitle && <InfoTooltip
                  overlayClassName="ToolTip-Forcast"
                  title={
                    <div className="report-info">
                      {infoTitle}
                    </div>
                  }
                >
                  <InfoCircleOutlined className='Info-withTitle-3' />
                </InfoTooltip>}
              </div>
            )}
          </span>
          <div className="card-nav">
            <ul>
              {data?.checkbox === "YES" ?
                <li>
                  <Checkbox
                    checked={isQualified === "YES"}
                    onChange={(e) => handleQualifiedChange2(e.target.checked ? "YES" : "NO")}
                  >
                    <span style={{ fontSize: 10 }}>{data?.checkboxLable}</span>
                  </Checkbox>
                </li> : ""}
              {data?.graphDatas?.dateFilter?.isFilter === "Yes" && <>
                <li className={data?.graphDatas?.dateFilter?.filterType === 'all' ? 'active' : 'deactivate'} >
                  <Link to="#" onClick={() => handleActiveChange(data.graphId, 'all')}>
                    All
                  </Link>
                </li>
                {data?.chartTypeAdd === "LegendIcon" ?
                  <li className={data?.graphDatas?.dateFilter?.filterType === 'today' ? 'active' : 'deactivate'} >
                    <Link to="#" onClick={() => handleActiveChange(data.graphId, 'today')}>
                      Today
                    </Link>
                  </li> : ""
                }
                <li className={data?.graphDatas?.dateFilter?.filterType === 'this_week' ? 'active' : 'deactivate'}>
                  <Link to="#" onClick={() => handleActiveChange(data.graphId, 'this_week')}>
                    Week
                  </Link>
                </li>
                <li className={data?.graphDatas?.dateFilter?.filterType === 'this_month' ? 'active' : 'deactivate'}>
                  <Link to="#" onClick={() => handleActiveChange(data.graphId, 'this_month')}>
                    Month
                  </Link>
                </li>
                <li className={data?.graphDatas?.dateFilter?.filterType === 'this_year' ? 'active' : 'deactivate'}>
                  <Link to="#" onClick={() => handleActiveChange(data.graphId, 'this_year')}>
                    FY
                  </Link>
                </li></>}
              <li>
                {showMaximize && (<button
                  type="button"
                  className="maximize-btn"
                  onClick={() => setMaximized(true)}
                  title="Maximize"
                  style={{ width: 26, height: 26, borderRadius: 7, border: "1px solid #E5E7EB", background: "transparent", color: "#9CA3AF", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginLeft: 8, padding: 0 }}
                >
                  <MaximizeIcon />
                </button>)}
              </li>
              {pagefrom === "Home" ?
                actions
                : (openPage ? <li>
                  <Popover
                    placement="bottomLeft"
                    content={<Content graphDatas={!graphDataStandardIndex ? graphopenUID : graphDataStandardIndex} graphDataIndex={graphDataIndex} name={data.graphName} id={graphopenUID} />}
                    trigger="hover"
                    open={openPage[graphDataStandardIndex ? graphopenUID : graphDataIndex]}
                    onOpenChange={() => handleOpenPageChange(graphDataStandardIndex ? graphopenUID : graphDataIndex)}
                  >
                    <FeatherIcon icon="more-vertical" size={20} style={{}} />
                  </Popover>
                </li> : <></>)}
            </ul>
          </div>
        </div>)}
        {/* Body */}
        <div style={{ display: "flex", flex: 1, overflow: "hidden", minHeight: 0 }}>
          {COMPAT && (<IconSidebar initialType={initialType} activeType={activeType} COMPAT={COMPAT} onSwitch={t => { setActiveType(t); }} />)}
          <div style={{ flex: 1, padding: "10px 8px", minWidth: 0, overflow: "hidden", position: "relative" }}>
            <ChartRenderer type={activeType} ogData={data} data={isPieish ? data.graphDatas : pagedGraphData} currencySettings={initialType === "Bar+Line" ? data.currecny : currencySettings} height={height} column={data.graphColumnIsCurrency} row1={data.graphRowIsCurrecny} row={data.graphGroupIsCurrecny} />
          </div>
          {showLegend && <LegendPanel type={activeType} data={data} showonlyLegend={showonlyLegend} currencySettings={currencySettings} showLegendValues={showLegendValues} hideLegendLabels={hideLegendLabels} />}
        </div>
        {(hasPager && totalPages > 1) && <Pager page={currentPage} total={totalPages} itemsPerPage={pageSize} totalItems={totalItems}
          onPrev={() => {
            setPaginationState((prev) => ({
              ...prev,
              [data.graphId]: Math.max(0, (prev[data.graphId] ?? 0) - 1),
            }));
          }}
          onNext={() => {
            setPaginationState((prev) => ({
              ...prev,
              [data.graphId]: Math.min(totalPages - 1, (prev[data.graphId] ?? 0) + 1),
            }));
          }}
        />}
      </div>
      {maximized && (
        <MaximizeModal hasPager={hasPager} totalPages={totalPages} totalItems={totalItems} initialType={initialType}
          title={data.graphName} paginationState={paginationState} currencySettings={initialType === "Bar+Line" ? data.currecny : currencySettings}
          data={data} page={currentPage} pageSize={pageSize} showonlyLegend={showonlyLegend}
          showLegendValues={showLegendValues} hideLegendLabels={hideLegendLabels}
          activeType={activeType} setPaginationState={setPaginationState}
          onClose={() => setMaximized(false)}
        />
      )}
    </>
  );
}
ChartCard.propTypes = {
  handleQualifiedChange2: PropTypes.func.isRequired,
  isQualified: PropTypes.string.isRequired,
  COMPAT: PropTypes.arrayOf(PropTypes.string),
  infoTitle: PropTypes.string.isRequired,
  paginationState: PropTypes.object.isRequired,
  setPaginationState: PropTypes.func.isRequired,
  currencySettings: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  initialType: PropTypes.string.isRequired,
  pageSize: PropTypes.number,
  height: PropTypes.number,
  style: PropTypes.object,
  delay: PropTypes.number,
  graphDataIndex: PropTypes.number,
  showLegend: PropTypes.bool,
  showLegendValues: PropTypes.bool,
  hideLegendLabels: PropTypes.bool,
  showPager: PropTypes.bool,
  showHeader: PropTypes.bool,
  showMaximize: PropTypes.bool,
  showonlyLegend: PropTypes.bool,
  handleActiveChange: PropTypes.func.isRequired,
  openPage: PropTypes.object.isRequired,
  handleOpenPageChange: PropTypes.func.isRequired,
  pagefrom: PropTypes.string.isRequired,
  actions: PropTypes.object.isRequired,
  Content: PropTypes.func.isRequired,
  graphopenUID: PropTypes.number.isRequired,
  graphDataStandardIndex: PropTypes.number.isRequired,
  titleNode: PropTypes.node,
};
ChartCard.defaultProps = {
  pageSize: null,
  height: 220,
  style: {},
  delay: 0,
};
export default function CRMCharts({ infoTitle, data, pageSize, handleActiveChange, COMPAT, graphopenUID,
  pagefrom, setPaginationState, handleQualifiedChange2, isQualified,
  actions, Content, showMaximize, showHeader, showonlyLegend, showLegend = true, showLegendValues = true, hideLegendLabels = false,
  openPage, paginationState, graphDataStandardIndex,
  handleOpenPageChange, titleNode, }) {
  useEffect(() => { injectCSS(); }, []);
  const localData = useSelector((state) => state.localRedux);
  const defaultCurrency = localData.currency
  const cardHeight = 45 + 300 + 45;
  return (
    <>
      {data ? <ChartCard delay={10} showonlyLegend={showonlyLegend} showLegend={showLegend} showLegendValues={showLegendValues} hideLegendLabels={hideLegendLabels} graphDataStandardIndex={graphDataStandardIndex} setPaginationState={setPaginationState} infoTitle={infoTitle} data={data} initialType={SubName[data.graphType]} pageSize={pageSize} height={300}
        handleActiveChange={handleActiveChange} pagefrom={pagefrom} actions={actions} openPage={openPage} Content={Content} graphopenUID={graphopenUID} showHeader={showHeader} handleQualifiedChange2={handleQualifiedChange2} isQualified={isQualified}
        handleOpenPageChange={handleOpenPageChange} paginationState={paginationState} COMPAT={COMPAT} currencySettings={defaultCurrency} showMaximize={showMaximize} titleNode={titleNode} />
        :
        <div className="crm-card" style={{ marginBottom: 25, background: "#FFFFFF", borderRadius: 16, border: "0.1px solid #cdcdcd", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 1px 10px #00000026", animationDelay: `${10} ms`, height: cardHeight, minHeight: cardHeight, maxHeight: cardHeight, }}>
          <div className="spin">
            <Spin />
          </div>
        </div >}
    </>
  );
}
CRMCharts.propTypes = {
  handleQualifiedChange2: PropTypes.func.isRequired,
  isQualified: PropTypes.string.isRequired,
  graphopenUID: PropTypes.number.isRequired,
  infoTitle: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  pageSize: PropTypes.number,
  handleActiveChange: PropTypes.func.isRequired,
  openPage: PropTypes.object.isRequired,
  handleOpenPageChange: PropTypes.func.isRequired,
  pagefrom: PropTypes.string.isRequired,
  actions: PropTypes.object.isRequired,
  paginationState: PropTypes.object.isRequired,
  setPaginationState: PropTypes.func.isRequired,
  Content: PropTypes.func.isRequired,
  COMPAT: PropTypes.arrayOf(PropTypes.string),
  showMaximize: PropTypes.bool.isRequired,
  showHeader: PropTypes.bool.isRequired,
  graphDataStandardIndex: PropTypes.number.isRequired,
  showonlyLegend: PropTypes.bool.isRequired,
  showLegend: PropTypes.bool,
  showLegendValues: PropTypes.bool,
  hideLegendLabels: PropTypes.bool,
  titleNode: PropTypes.node,
};